/**
 * <equalize-options>
 * ------------------------------------------------------------------------------------------------
 * Makes all `.block-swatch` option boxes inside it render at the same width — the width of the
 * widest box — regardless of label length. The boxes still hug that content width, so the row
 * is NOT stretched to full width.
 *
 * Used to keep option boxes (e.g. the "Stil" option) visually consistent on mobile and desktop.
 *
 * The custom element's connectedCallback re-runs every time the element is (re-)inserted into the
 * DOM — including after variant changes, combined-listings swaps, quick-buy drawers and Section
 * Rendering API updates — so the equalisation stays correct without extra wiring.
 */
if (!customElements.get('equalize-options')) {
  customElements.define(
    'equalize-options',
    class EqualizeOptions extends HTMLElement {
      connectedCallback() {
        this._equalize = this._equalize.bind(this);

        // First pass once layout is ready, then again once webfonts have loaded (label widths
        // shift when the custom font swaps in).
        requestAnimationFrame(this._equalize);
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(this._equalize);
        }

        // Recompute on viewport changes (breakpoint padding differs between mobile and desktop).
        window.addEventListener('resize', this._equalize, { passive: true });
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this._equalize);
      }

      _equalize() {
        const items = this.querySelectorAll('.block-swatch');
        if (items.length < 2) return;

        // Reset any previously applied width so we can read each box's natural content width.
        items.forEach((el) => {
          el.style.minWidth = '';
        });

        let max = 0;
        items.forEach((el) => {
          max = Math.max(max, el.getBoundingClientRect().width);
        });

        // Skip while hidden (rect width 0) — connectedCallback / resize will retry when visible.
        if (max <= 0) return;

        const width = Math.ceil(max) + 'px';
        items.forEach((el) => {
          el.style.minWidth = width;
        });
      }
    }
  );
}
